import {BaseApi} from './BaseApi'
import {reportCatchError} from '../utils/errorReporter'

/**
 * Paystub Status Enum
 */
// TODO: Migrate to PascalCase values once BE runs DB migration script
export enum PaystubStatus {
  Pending = 'pending',
  Approved = 'approved',
  Rejected = 'rejected',
  Voided = 'voided',
  /** Reversed by an overpayment-recovery correction batch (distinct from a plain void). */
  Reversed = 'reversed',
}

/**
 * Paystub Earning Response
 */
export interface PaystubEarning {
  paycodeId: string
  paycodeName: string
  systemCode: string
  units: number
  unitType: string
  rate: number
  amount: number
  /** Year-to-date hours for this paycode (including this paystub). */
  ytdHours?: number
  /** Year-to-date amount for this paycode (including this paystub). */
  ytdAmount?: number
  isTip?: boolean
  tipBehavior?: 'CASH' | 'PAID'
  affectsNetPay?: boolean
  /**
   * Per-line assignment/location context for merged (multi-assignment) paystubs.
   * Optional: absent on paystubs generated before per-assignment merging — callers
   * should fall back to the paystub-level department/company in that case.
   */
  assignmentId?: string
  companyName?: string
  /** App-defined customer label (company for mspservice); falls back to companyName. */
  customerName?: string
  siteName?: string
  departmentName?: string
  /**
   * Source invoice item and the work week this line was earned in (ISO dates, UTC midnight).
   * Back-pay lines (isPriorPeriod) show their own week instead of the paystub's pay period.
   * Absent on paystubs generated before these fields existed.
   */
  invoiceItemId?: string
  weekStartDate?: string
  weekEndDate?: string
  isPriorPeriod?: boolean
}

/**
 * One time-off type on the paystub's time-off snapshot (hours). Frozen when the
 * paystub is generated: used this check, used year to date (same total as the
 * earnings line's ytdHours) and, for sick only, the hours remaining.
 */
export interface PaystubTimeOffLine {
  type: 'Sick' | 'Vacation' | 'Holiday' | string
  systemCode: string
  /** Label for the YTD-only earnings row, e.g. 'SICK'. */
  paycodeName: string
  usedThisPeriod: number
  ytdUsed: number
  ytdAmount: number
  /** Hours remaining; null when not known (shown as "-"). */
  endBalance?: number | null
  units: string
}

/** Paystub time-off snapshot; absent on paystubs generated before it. */
export interface PaystubTimeOff {
  asOfDate: string
  taxYear: number
  lines: PaystubTimeOffLine[]
}

/**
 * One earnings paycode paid earlier this year but not on this paystub, shown as a
 * YTD-only earnings row ("-" in the current-period columns). Frozen when the
 * paystub is generated.
 */
export interface PaystubYtdOnlyEarning {
  paycodeId: string
  systemCode: string
  paycodeName: string
  ytdHours: number
  ytdAmount: number
}

/**
 * Paystub Tax Response
 */
export interface PaystubTax {
  taxId: string
  taxType: string
  taxDescription: string
  amount: number
  /** Tax rate applied this pay period (decimal). */
  rate?: number
  /** Subject (taxable) wages for this pay period. */
  taxableWages?: number
  /** Year-to-date amount (including this paystub). */
  ytdAmount?: number
  /** Year-to-date subject (taxable) wages (including this paystub). */
  ytdTaxableWages?: number
}

/**
 * Paystub Deduction Response
 */
export interface PaystubDeduction {
  code: string
  description: string
  legalCategory: string
  isPreTax: boolean
  calculatedAmount: number
  actualAmount: number
  shortfallAmount: number
  ytdAmount: number
  paycodeName?: string
  /** True when this line is a reimbursement/credit (money ADDED to net, not subtracted). */
  isCredit?: boolean
  /**
   * Remaining balance carried into the next check for a declining-balance
   * deduction (e.g. Overpayment Recovery). 0 once fully collected.
   */
  carryForwardBalance?: number
}

/**
 * Paystub Reimbursement — money ADDED to net pay (never a taxable earning line).
 * Shown alongside deductions & garnishments. isTaxable === true means a pre-tax
 * paycode (included in gross + taxed); false means post-tax (added straight to net).
 */
export interface PaystubReimbursement {
  code: string
  description: string
  amount: number
  isTaxable: boolean
  paycodeId?: string
  paycodeName?: string
  ytdAmount?: number
  assignmentId?: string
  siteName?: string
  departmentName?: string
}

/**
 * Paystub Garnishment Response
 */
export interface PaystubGarnishment {
  type: string
  caseNumber?: string
  calculatedAmount: number
  actualAmount: number
  ytdAmount?: number
  remainingBalance?: number
  paycodeName?: string
}

export interface WithholdingElection {
  filingStatus?: string
  totalExemptions?: number
  additionalWithholding?: number
  isExempt?: boolean
  state?: string
}

export interface WithholdingElections {
  federal?: WithholdingElection
  stateElection?: WithholdingElection
}

/**
 * Per-item payment tracking from payroll microservice webhooks.
 */
export interface PaystubPaymentItem {
  externalItemId: number
  paymentMethod?: string
  paymentAmount?: number
  paymentStatus?: string
  checkNumber?: string
  itemStatus?: string
  reasonType?: string
  reasonNote?: string
  submittedToBankAt?: string
  stopCheckNote?: string
  stopCheckReferenceNumber?: string
  stopCheckRequestedAt?: string
  voidReason?: string
  voidedAt?: string
  achReversalNote?: string
  achReversalRequestedAt?: string
  lastWebhookAt?: string
}

/**
 * Paystub Response - Full paystub data
 */
export interface PaystubResponse {
  _id: string
  invoiceId: string
  invoiceItemId: string
  batchId: string
  applicantId: string
  agencyId: string
  companyId: string
  assignmentId?: string
  /** STFFLG-3575: worksite the assignment belongs to (set when the API includes site enrichment). */
  siteId?: string
  /** STFFLG-3575: worksite name for the paycheck header. */
  siteName?: string
  /** STFFLG-3575: formatted worksite address for the paycheck header. */
  siteAddress?: string
  /** Name of the EOR (agencyId) that issued the check, resolved by the API for the Paychecks tab. */
  eorName?: string
  /**
   * Candidate Paychecks tab: the caller may list this paystub but not read it, because none of their
   * agencies placed the work on it. Only _id, payDate, externalCheckDate, createdDate and eorName are
   * set; every other field is absent, whatever this type declares.
   */
  restricted?: boolean
  employeeName: string
  firstName: string
  lastName: string
  ssnLast4: string
  employeeNumber?: string
  payPeriodStart: string
  payPeriodEnd: string
  payDate: string
  /**
   * Check date sourced from the external payroll system (e.g. Payology) when the
   * user changed it there. Preferred over payDate for the displayed Check Date.
   */
  externalCheckDate?: string
  payFrequency: string
  status: PaystubStatus
  calculatedAt: string
  approvedAt?: string
  approvedBy?: string
  rejectionReason?: string
  // ── Overpayment-recovery correction linkage ──────────────────────────────
  /** True on a corrected (replacement) paystub — non-disbursing (Net Disbursed $0). */
  isCorrection?: boolean
  /** On a corrected paystub: the original (reversed) paystub it replaces. */
  correctionOfPaystubId?: string
  /** On a reversed original paystub: the correction batch that reversed it. */
  correctionBatchId?: string
  reversedAt?: string
  reversedBy?: string
  /**
   * On a corrected (non-disbursing) paystub: the overpayment scheduled for
   * recovery = original net paid − corrected net. Documented on the check even
   * though nothing is collected here (collection begins next paycheck).
   */
  overpaymentOwed?: number
  // Earnings
  earnings: PaystubEarning[]
  grossWages: number
  // Taxes
  employeeTaxes: PaystubTax[]
  totalEmployeeTaxes: number
  totalEmployerTaxes: number
  // Deductions
  preTaxDeductions: PaystubDeduction[]
  totalPreTaxDeductions: number
  postTaxDeductions: PaystubDeduction[]
  totalPostTaxDeductions: number
  // Garnishments
  garnishments: PaystubGarnishment[]
  totalGarnishments: number
  // Reimbursements (money added to net; shown with deductions & garnishments)
  reimbursements?: PaystubReimbursement[]
  totalReimbursements?: number
  totalTaxableReimbursements?: number
  // Net Pay
  netPay: number
  paymentMethod: string
  checkNumber?: string
  /**
   * SHR-3865: BE-enriched, response-only payment/bank breakdown for the paystub.
   * One row per direct-deposit (ACH) account with the amount deposited this pay
   * period, or a single 'Check' row when the employee is paid by check. Account
   * numbers are masked in the BE ('####' + last 4); raw account/routing numbers
   * are never sent to the FE. Rendered at the bottom of the paystub.
   */
  paymentBreakdown?: {label: string; amount: number; accountNo: string}[]
  /** True when net pay was floored at $0 because taxes on tips exceeded available wages. */
  netPayFloored?: boolean
  /** Net pay before the $0 floor (negative when there is uncollected tax). */
  netPayBeforeFloor?: number
  // Uncollected employee tax on tips (W-2 Box 12)
  /** Uncollected employee Social Security tax on tips — W-2 Box 12 Code A. */
  uncollectedSocialSecurityTaxTips?: number
  /** Uncollected employee Medicare tax on tips — W-2 Box 12 Code B. */
  uncollectedMedicareTaxTips?: number
  /** Uncollected employee Additional Medicare tax on tips (Form 8959; no W-2 box). */
  uncollectedAdditionalMedicareTaxTips?: number
  /** Uncollected income tax on tips (informational; not on W-2). */
  uncollectedIncomeTaxTips?: number
  // YTD Totals
  ytdGrossWages: number
  ytdFederalTax: number
  ytdStateTax: number
  ytdFICA: number
  ytdMedicare: number
  ytdSDI: number
  ytdNetPay: number
  /** YTD uncollected Social Security tax on tips — W-2 Box 12 Code A. */
  ytdUncollectedSocialSecurityTaxTips?: number
  /** YTD uncollected Medicare tax on tips — W-2 Box 12 Code B. */
  ytdUncollectedMedicareTaxTips?: number
  /** YTD uncollected Additional Medicare tax on tips. */
  ytdUncollectedAdditionalMedicareTaxTips?: number
  /** YTD uncollected income tax on tips (informational). */
  ytdUncollectedIncomeTaxTips?: number
  /** YTD pre-tax wellness contributions (Emmy Health). */
  ytdWellness?: number
  /** YTD post-tax claim reimbursements (Emmy Health). */
  ytdClaim?: number
  /** YTD non-taxable expense reimbursement earnings. */
  ytdReimbursement?: number
  // Withholding elections snapshot
  withholdingElections?: WithholdingElections
  /** Time-off snapshot (taken YTD + sick balance); absent on older paystubs. */
  timeOff?: PaystubTimeOff
  /** YTD-only earnings rows; absent on paystubs generated before them. */
  ytdOnlyEarnings?: PaystubYtdOnlyEarning[]
  // Other
  locationCode?: string
  calculationError?: string
  notes?: string
  paidAt?: string
  paidBy?: string
  externalPayrollBatchId?: string
  externalPaymentStatus?: string
  paymentStatusReason?: string
  paymentStatusReasonNote?: string
  paymentStatusChangedAt?: string
  paymentIssue?: boolean
  paymentItems?: PaystubPaymentItem[]
  createdDate: string
  modifiedDate: string
  /**
   * Set by candidate-portal paychecks API for self-service modal header.
   * @deprecated STFFLG-3575: the paycheck header now shows the site (worksite)
   * via siteName/siteAddress instead of the parent company.
   */
  companyName?: string
  companyAddress?: string
}

/**
 * Paystub List Response with pagination
 */
export interface PaystubListResponse {
  paystubs: PaystubResponse[]
  total: number
  page: number
  limit: number
  totalPages: number
}

/**
 * Paystub Summary Response
 */
export interface PaystubSummaryResponse {
  totalPaystubs: number
  pendingCount: number
  approvedCount: number
  rejectedCount: number
  voidedCount: number
  totalGrossWages: number
  totalEmployeeTaxes: number
  totalEmployerTaxes: number
  totalNetPay: number
}

/**
 * Paystub Filter Options
 */
export interface PaystubFilters {
  id?: string
  invoiceId?: string
  batchId?: string
  applicantId?: string
  agencyId?: string
  companyId?: string
  status?: PaystubStatus
  payPeriodStartFrom?: string
  payPeriodStartTo?: string
  page?: number
  limit?: number
}

/**
 * Approve Paystub Request
 */
export interface ApprovePaystubRequest {
  notes?: string
}

/**
 * Reject Paystub Request
 */
export interface RejectPaystubRequest {
  rejectionReason: string
}

/**
 * Void Paystub Request
 */
export interface VoidPaystubRequest {
  reason: string
}

/**
 * Update Paystub Notes Request
 */
export interface UpdatePaystubNotesRequest {
  notes: string
}

/**
 * Create Paystub Deduction Request
 * Based on backend CreatePaystubDeductionRequest DTO
 */
export interface CreatePaystubDeductionRequest {
  paycodeId: string // Required - the paycode ID to use
  amount: number // Required - the deduction amount
  caseNumber?: string // Optional - required for garnishments
}

/**
 * Update Paystub Deduction Request
 * STFFLG-3207: Fields must match backend DTO (calculatedAmount/actualAmount, not amount)
 */
export interface UpdatePaystubDeductionRequest {
  calculatedAmount?: number
  actualAmount?: number
  description?: string
}

/**
 * Deduction Response from API
 */
export interface DeductionResponse {
  id: string
  paystubId: string
  paycodeId: string
  code: string
  description: string
  legalCategory: string
  isPreTax: boolean
  amount: number
  caseNumber?: string
  ytdAmount: number
  createdDate: string
  modifiedDate: string
}

/**
 * Paystubs API Service
 * Handles all paystub-related API calls
 * Extends BaseApi for automatic Bearer token authorization
 */
export class PaystubsApi extends BaseApi {
  // ==================== HEALTH CHECK ====================

  /**
   * Health check for paystub routes
   * GET /payroll/paystubs/health
   */
  async healthCheck(): Promise<{status: string}> {
    try {
      const response = await this.client.get<{status: string}>('/payroll/paystubs/health')
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error checking paystubs health:', error)
      throw error
    }
  }

  // ==================== GET PAYSTUBS ====================

  /**
   * Get paystubs with filters and pagination
   * GET /payroll/paystubs
   *
   * @param filters - Optional filter parameters
   */
  async getPaystubs(filters?: PaystubFilters): Promise<PaystubListResponse> {
    try {
      const params = new URLSearchParams()
      if (filters?.id) params.append('id', filters.id)
      if (filters?.invoiceId) params.append('invoiceId', filters.invoiceId)
      if (filters?.batchId) params.append('batchId', filters.batchId)
      if (filters?.applicantId) params.append('applicantId', filters.applicantId)
      if (filters?.agencyId) params.append('agencyId', filters.agencyId)
      if (filters?.companyId) params.append('companyId', filters.companyId)
      if (filters?.status) params.append('status', filters.status)
      if (filters?.payPeriodStartFrom)
        params.append('payPeriodStartFrom', filters.payPeriodStartFrom)
      if (filters?.payPeriodStartTo) params.append('payPeriodStartTo', filters.payPeriodStartTo)
      if (filters?.page) params.append('page', filters.page.toString())
      if (filters?.limit) params.append('limit', filters.limit.toString())

      const response = await this.client.get<PaystubListResponse>(
        `/payroll/paystubs${params.toString() ? `?${params.toString()}` : ''}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching paystubs:', error)
      throw error
    }
  }

  /**
   * Get paystub summary for an invoice
   * GET /payroll/paystubs/invoice/:invoiceId/summary
   *
   * @param invoiceId - The invoice ID
   */
  async getPaystubSummary(invoiceId: string): Promise<PaystubSummaryResponse> {
    try {
      const response = await this.client.get<PaystubSummaryResponse>(
        `/payroll/paystubs/invoice/${invoiceId}/summary`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching paystub summary:', error)
      throw error
    }
  }

  /**
   * Get all paystubs for an invoice
   * GET /payroll/paystubs/invoice/:invoiceId
   *
   * @param invoiceId - The invoice ID
   */
  async getPaystubsByInvoice(invoiceId: string): Promise<PaystubResponse[]> {
    try {
      const response = await this.client.get<PaystubResponse[]>(
        `/payroll/paystubs/invoice/${invoiceId}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching paystubs for invoice:', error)
      throw error
    }
  }

  /**
   * Get paystub by ID
   * GET /payroll/paystubs/:paystubId
   *
   * @param paystubId - The paystub ID
   */
  async getPaystubById(paystubId: string): Promise<PaystubResponse> {
    try {
      const response = await this.client.get<PaystubResponse>(`/payroll/paystubs/${paystubId}`)
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error fetching paystub:', error)
      throw error
    }
  }

  // ==================== PAYSTUB ACTIONS ====================

  /**
   * Approve all pending paystubs for an invoice
   * POST /payroll/paystubs/invoice/:invoiceId/approve-all
   *
   * @param invoiceId - The invoice ID
   */
  async approveAllPaystubs(invoiceId: string): Promise<{approved: number; failed: number}> {
    try {
      const response = await this.client.post<{approved: number; failed: number}>(
        `/payroll/paystubs/invoice/${invoiceId}/approve-all`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error approving all paystubs:', error)
      throw error
    }
  }

  /**
   * Approve a pending paystub
   * POST /payroll/paystubs/:paystubId/approve
   *
   * @param paystubId - The paystub ID
   * @param request - Optional notes
   */
  async approvePaystub(
    paystubId: string,
    request?: ApprovePaystubRequest,
  ): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/approve`,
        request || {},
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error approving paystub:', error)
      throw error
    }
  }

  /**
   * Reject a pending paystub
   * POST /payroll/paystubs/:paystubId/reject
   *
   * @param paystubId - The paystub ID
   * @param request - Rejection reason
   */
  async rejectPaystub(paystubId: string, request: RejectPaystubRequest): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/reject`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error rejecting paystub:', error)
      throw error
    }
  }

  /**
   * Void an approved paystub
   * POST /payroll/paystubs/:paystubId/void
   *
   * @param paystubId - The paystub ID
   * @param request - Void reason
   */
  async voidPaystub(paystubId: string, request: VoidPaystubRequest): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/void`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error voiding paystub:', error)
      throw error
    }
  }

  /**
   * Update paystub notes
   * PATCH /payroll/paystubs/:paystubId/notes
   *
   * @param paystubId - The paystub ID
   * @param request - Notes to update
   */
  async updatePaystubNotes(
    paystubId: string,
    request: UpdatePaystubNotesRequest,
  ): Promise<PaystubResponse> {
    try {
      const response = await this.client.patch<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/notes`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating paystub notes:', error)
      throw error
    }
  }

  /**
   * Recalculate taxes for a pending paystub
   * POST /payroll/paystubs/:paystubId/recalculate
   *
   * @param paystubId - The paystub ID
   */
  async recalculatePaystub(paystubId: string): Promise<PaystubResponse> {
    try {
      const response = await this.client.post<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/recalculate`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error recalculating paystub:', error)
      throw error
    }
  }

  // ==================== DEDUCTION MANAGEMENT ====================

  /**
   * Add a deduction to a paystub
   * POST /payroll/paystubs/:paystubId/deductions
   *
   * The backend determines whether it's a pre-tax, post-tax, or garnishment
   * deduction based on the paycode's legalCategory.
   *
   * @param paystubId - The paystub ID
   * @param request - The deduction data (paycodeId, amount, optional caseNumber)
   */
  async addDeduction(
    paystubId: string,
    request: CreatePaystubDeductionRequest,
  ): Promise<DeductionResponse> {
    try {
      const response = await this.client.post<DeductionResponse>(
        `/payroll/paystubs/${paystubId}/deductions`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error adding deduction to paystub:', error)
      throw error
    }
  }

  /**
   * Update a deduction on a paystub
   * PUT /payroll/paystubs/:paystubId/deductions/:index?type=pretax|posttax|garnishment|reimbursement
   *
   * @param paystubId - The paystub ID
   * @param index - The index of the deduction in its array (0-based)
   * @param type - The type of deduction: 'pretax', 'posttax', 'garnishment', or 'reimbursement'
   * @param request - The updated deduction data
   */
  async updateDeduction(
    paystubId: string,
    index: number,
    type: 'pretax' | 'posttax' | 'garnishment' | 'reimbursement',
    request: UpdatePaystubDeductionRequest,
  ): Promise<DeductionResponse> {
    try {
      const response = await this.client.put<DeductionResponse>(
        `/payroll/paystubs/${paystubId}/deductions/${index}?type=${type}`,
        request,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error updating deduction:', error)
      throw error
    }
  }

  /**
   * Remove a deduction from a paystub
   * DELETE /payroll/paystubs/:paystubId/deductions/:index?type=pretax|posttax|garnishment|reimbursement
   *
   * @param paystubId - The paystub ID
   * @param index - The index of the deduction in its array (0-based)
   * @param type - The type of deduction: 'pretax', 'posttax', 'garnishment', or 'reimbursement'
   * @returns The updated paystub after removing the deduction
   */
  async removeDeduction(
    paystubId: string,
    index: number,
    type: 'pretax' | 'posttax' | 'garnishment' | 'reimbursement',
  ): Promise<PaystubResponse> {
    try {
      const response = await this.client.delete<PaystubResponse>(
        `/payroll/paystubs/${paystubId}/deductions/${index}?type=${type}`,
      )
      return response.data
    } catch (error: unknown) {
      reportCatchError('Error removing deduction:', error)
      throw error
    }
  }

  /**
   * Download one or more paystubs as a PDF or ZIP.
   * POST /payroll/paystubs/download-pdf
   *
   * - 1–100 paystub IDs required (validated server-side).
   * - outputMode 'single' (default) → merged PDF; 'multiple' → ZIP of individual PDFs.
   * - Returns 403 with a generic message if any id is missing or not accessible
   *   (all-or-nothing; the caller should surface the error to the user).
   * - Filename comes from the Content-Disposition header.
   */
  async downloadPaystubsPdf(body: {
    paystubIds: string[]
    outputMode?: 'single' | 'multiple'
  }): Promise<{blob: Blob; contentDisposition: string}> {
    try {
      const response = await this.client.post('/payroll/paystubs/download-pdf', body, {
        responseType: 'blob',
      })
      return {
        blob: response.data as Blob,
        contentDisposition: (response.headers['content-disposition'] as string) ?? '',
      }
    } catch (error: unknown) {
      reportCatchError('Error downloading paystubs PDF:', error)
      const message = await this.extractBlobErrorMessage(error)
      throw message ? new Error(message) : error
    }
  }
}

// Export a singleton instance
export const paystubsApi = new PaystubsApi()

export default paystubsApi
